import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  Snackbar,
  Alert,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Fab,
  MenuItem,
  Divider,
  Chip,
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useTheme, useMediaQuery } from '@mui/material';
import { apiService } from '../../services/api';
import type { PigmentoDetalhado, NotaFiscalPigmento } from '../../types';

interface PigmentoFormData {
  nome: string;
  codigo?: string;
  cor_hex?: string;
  categoria?: string;
  composicao?: string;
  preco_unitario: number | string;
  estoque_atual: number | string;
  estoque_minimo: number | string;
  notas_fiscais: NotaFiscalPigmento[];
}

// Componente memoizado para row de nota fiscal
const NotaFiscalRow = memo(({ index, register, control, remove, errors }: any) => {
  const { fields: caixasFields, append: appendCaixa, remove: removeCaixa } = useFieldArray({
    control,
    name: `notas_fiscais.${index}.caixas_lote` as const,
  });

  return (
    <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Nota Fiscal #{index + 1}
        </Typography>
        <IconButton size="small" color="error" onClick={() => remove(index)}>
          <DeleteIcon />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
            <TextField
              fullWidth
              label="Número da Nota Fiscal"
              {...register(`notas_fiscais.${index}.numero_nota_fiscal`)}
              error={!!errors?.notas_fiscais?.[index]?.numero_nota_fiscal}
              helperText={errors?.notas_fiscais?.[index]?.numero_nota_fiscal?.message}
              size="small"
            />
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
            <TextField
              fullWidth
              label="Empresa Fornecedor"
              {...register(`notas_fiscais.${index}.empresa_fornecedor`)}
              error={!!errors?.notas_fiscais?.[index]?.empresa_fornecedor}
              helperText={errors?.notas_fiscais?.[index]?.empresa_fornecedor?.message}
              size="small"
            />
          </Box>
        </Box>
        <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
          <TextField
            fullWidth
            label="Data de Entrada"
            type="date"
            {...register(`notas_fiscais.${index}.data_entrada`)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
        Caixas/Lotes
      </Typography>

      {caixasFields.map((caixa, caixaIndex) => (
        <Paper key={caixa.id} sx={{ p: 2, mb: 1, bgcolor: 'white', border: '1px solid', borderColor: 'grey.300' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Caixa/Lote #{caixaIndex + 1}
            </Typography>
            <IconButton size="small" onClick={() => removeCaixa(caixaIndex)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 45%', minWidth: '150px' }}>
                <TextField
                  fullWidth
                  label="Lote"
                  {...register(`notas_fiscais.${index}.caixas_lote.${caixaIndex}.lote`)}
                  error={!!errors?.notas_fiscais?.[index]?.caixas_lote?.[caixaIndex]?.lote}
                  helperText={errors?.notas_fiscais?.[index]?.caixas_lote?.[caixaIndex]?.lote?.message}
                  size="small"
                />
              </Box>
              <Box sx={{ flex: '1 1 45%', minWidth: '150px' }}>
                <TextField
                  fullWidth
                  label="Validade"
                  type="date"
                  {...register(`notas_fiscais.${index}.caixas_lote.${caixaIndex}.validade`)}
                  error={!!errors?.notas_fiscais?.[index]?.caixas_lote?.[caixaIndex]?.validade}
                  helperText={errors?.notas_fiscais?.[index]?.caixas_lote?.[caixaIndex]?.validade?.message}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 30%', minWidth: '100px' }}>
                <TextField
                  fullWidth
                  label="Peso Bruto (kg)"
                  type="number"
                  {...register(`notas_fiscais.${index}.caixas_lote.${caixaIndex}.peso_bruto`)}
                  inputProps={{ step: '0.01', min: '0' }}
                  size="small"
                />
              </Box>
              <Box sx={{ flex: '1 1 30%', minWidth: '100px' }}>
                <TextField
                  fullWidth
                  label="Peso Líquido (kg)"
                  type="number"
                  {...register(`notas_fiscais.${index}.caixas_lote.${caixaIndex}.peso_liquido`)}
                  inputProps={{ step: '0.01', min: '0' }}
                  size="small"
                />
              </Box>
              <Box sx={{ flex: '1 1 30%', minWidth: '100px' }}>
                <Controller
                  name={`notas_fiscais.${index}.caixas_lote.${caixaIndex}.status`}
                  control={control}
                  defaultValue="disponivel"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      select
                      label="Status"
                      size="small"
                    >
                      <MenuItem value="disponivel">Disponível</MenuItem>
                      <MenuItem value="em_uso">Em Uso</MenuItem>
                      <MenuItem value="esgotada">Esgotada</MenuItem>
                    </TextField>
                  )}
                />
              </Box>
            </Box>
          </Box>
        </Paper>
      ))}

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => appendCaixa({
          lote: '',
          validade: '',
          peso_bruto: '',
          peso_liquido: '',
          status: 'disponivel',
        })}
        sx={{ mt: 1 }}
      >
        Adicionar Caixa/Lote
      </Button>
    </Paper>
  );
});

NotaFiscalRow.displayName = 'NotaFiscalRow';

const Pigmentos: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [pigmentos, setPigmentos] = useState<PigmentoDetalhado[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPigmento, setEditingPigmento] = useState<PigmentoDetalhado | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<PigmentoFormData>({
    defaultValues: {
      nome: '',
      codigo: '',
      categoria: '',
      composicao: '',
      preco_unitario: '',
      estoque_atual: '',
      estoque_minimo: '',
      notas_fiscais: [],
    },
  });

  const { fields: notasFields, append: appendNota, remove: removeNota } = useFieldArray({
    control,
    name: 'notas_fiscais',
  });

  const showMessage = useCallback((message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const loadPigmentos = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getPigmentosDetalhados();
      setPigmentos(data);
    } catch (error) {
      showMessage('Erro ao carregar pigmentos', 'error');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    loadPigmentos();
  }, [loadPigmentos]);

  const handleOpenDialog = useCallback(async (pigmento?: PigmentoDetalhado) => {
    if (pigmento) {
      // Buscar dados completos do pigmento com notas fiscais e caixas
      try {
        const pigmentoCompleto = await apiService.getPigmentoDetalhado(pigmento.id);
        if (pigmentoCompleto) {
          setEditingPigmento(pigmentoCompleto);
          reset({
            nome: pigmentoCompleto.nome,
            codigo: pigmentoCompleto.codigo || '',
            cor_hex: pigmentoCompleto.cor_hex || '',
            categoria: pigmentoCompleto.categoria || '',
            composicao: pigmentoCompleto.composicao || '',
            preco_unitario: pigmentoCompleto.preco_unitario,
            estoque_atual: pigmentoCompleto.estoque_atual,
            estoque_minimo: pigmentoCompleto.estoque_minimo,
            notas_fiscais: pigmentoCompleto.notas_fiscais || [],
          });
        }
      } catch (error) {
        showMessage('Erro ao carregar dados do pigmento', 'error');
        return;
      }
    } else {
      setEditingPigmento(null);
      reset({
        nome: '',
        codigo: '',
        categoria: '',
        composicao: '',
        preco_unitario: '',
        estoque_atual: '',
        estoque_minimo: '',
        notas_fiscais: [],
      });
    }
    setDialogOpen(true);
  }, [reset, showMessage]);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPigmento(null);
    clearErrors();
    reset();
  };

  const onSubmit = async (data: PigmentoFormData) => {
    clearErrors();

    try {
      const pigmentoData: any = {
        nome: data.nome,
        codigo: data.codigo || null,
        cor_hex: data.cor_hex || null,
        categoria: data.categoria || null,
        composicao: data.composicao || null,
        preco_unitario: Number(data.preco_unitario) || 0,
        estoque_atual: Number(data.estoque_atual) || 0,
        estoque_minimo: Number(data.estoque_minimo) || 0,
        notas_fiscais: data.notas_fiscais.map(nota => ({
          numero_nota_fiscal: nota.numero_nota_fiscal,
          empresa_fornecedor: nota.empresa_fornecedor,
          data_entrada: nota.data_entrada || null,
          caixas_lote: nota.caixas_lote.map(caixa => ({
            peso_bruto: Number(caixa.peso_bruto) || null,
            peso_liquido: Number(caixa.peso_liquido) || null,
            lote: caixa.lote,
            validade: caixa.validade,
            status: caixa.status || 'disponivel',
          })),
        })),
      };

      if (editingPigmento) {
        await apiService.updatePigmentoDetalhado(editingPigmento.id, pigmentoData);
        showMessage('Pigmento atualizado com sucesso!', 'success');
      } else {
        await apiService.createPigmentoDetalhado(pigmentoData);
        showMessage('Pigmento criado com sucesso!', 'success');
      }

      handleCloseDialog();
      loadPigmentos();
    } catch (error: any) {
      if (error?.fieldErrors) {
        Object.entries(error.fieldErrors).forEach(([field, message]) => {
          setError(field as any, { type: 'server', message: message as string });
        });
        showMessage('Verifique os campos destacados', 'error');
      } else {
        showMessage(error?.message || 'Erro ao salvar pigmento', 'error');
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja remover este pigmento?')) {
      try {
        await apiService.deletePigmentoDetalhado(id);
        showMessage('Pigmento removido com sucesso!', 'success');
        loadPigmentos();
      } catch (error) {
        showMessage('Erro ao remover pigmento', 'error');
      }
    }
  };

  const columns: GridColDef[] = [
    { field: 'nome', headerName: 'Nome', flex: 1, minWidth: 200 },
    { field: 'codigo', headerName: 'Código', width: 120 },
    { field: 'categoria', headerName: 'Categoria', width: 130 },
    {
      field: 'composicao',
      headerName: 'Composição',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'total_notas',
      headerName: 'Notas',
      width: 80,
      align: 'center',
      renderCell: (params) => (
        <Chip label={params.value || 0} size="small" color="primary" variant="outlined" />
      ),
    },
    {
      field: 'total_caixas',
      headerName: 'Caixas',
      width: 80,
      align: 'center',
      renderCell: (params) => (
        <Chip label={params.value || 0} size="small" color="secondary" variant="outlined" />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Ações',
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label="Editar"
          onClick={() => handleOpenDialog(params.row)}
        />,
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon />}
          label="Excluir"
          onClick={() => handleDelete(params.row.id)}
        />,
      ],
    },
  ];

  if (loading) {
    return <Typography>Carregando...</Typography>;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Pigmentos</Typography>
        {!isMobile && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Novo Pigmento
          </Button>
        )}
      </Box>

      {isMobile ? (
        <>
          {pigmentos.map((pigmento) => (
            <Card key={pigmento.id} sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6">{pigmento.nome}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Código: {pigmento.codigo || '-'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Categoria: {pigmento.categoria || '-'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Composição: {pigmento.composicao || '-'}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <Chip label={`${pigmento.total_notas || 0} notas`} size="small" color="primary" variant="outlined" />
                  <Chip label={`${pigmento.total_caixas || 0} caixas`} size="small" color="secondary" variant="outlined" />
                </Box>
              </CardContent>
              <CardActions>
                <Button size="small" startIcon={<EditIcon />} onClick={() => handleOpenDialog(pigmento)}>
                  Editar
                </Button>
                <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDelete(pigmento.id)}>
                  Excluir
                </Button>
              </CardActions>
            </Card>
          ))}

          <Fab
            color="primary"
            aria-label="add"
            sx={{ position: 'fixed', bottom: 16, right: 16 }}
            onClick={() => handleOpenDialog()}
          >
            <AddIcon />
          </Fab>
        </>
      ) : (
        <DataGrid
          rows={pigmentos}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          autoHeight
          disableRowSelectionOnClick
        />
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {editingPigmento ? 'Editar Pigmento' : 'Novo Pigmento'}
          <IconButton
            onClick={handleCloseDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Dados do Pigmento
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    label="Nome do Pigmento *"
                    {...register('nome', { required: 'Nome é obrigatório' })}
                    error={!!errors.nome}
                    helperText={errors.nome?.message}
                  />
                </Box>
                <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    label="Código"
                    {...register('codigo')}
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 45%', minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    label="Categoria"
                    {...register('categoria')}
                  />
                </Box>
              </Box>
              <TextField
                fullWidth
                label="Composição"
                multiline
                rows={3}
                {...register('composicao')}
                placeholder="Descreva a composição do pigmento..."
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Notas Fiscais e Caixas/Lotes</Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  appendNota({
                    numero_nota_fiscal: '',
                    empresa_fornecedor: '',
                    data_entrada: '',
                    caixas_lote: [],
                  })
                }
              >
                Adicionar Nota Fiscal
              </Button>
            </Box>

            {notasFields.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                Nenhuma nota fiscal cadastrada. Clique em "Adicionar Nota Fiscal" para começar.
              </Typography>
            )}

            {notasFields.map((field, index) => (
              <NotaFiscalRow
                key={field.id}
                index={index}
                register={register}
                control={control}
                remove={removeNota}
                errors={errors}
              />
            ))}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {editingPigmento ? 'Salvar' : 'Criar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Pigmentos;
